/** Receipts (M04-08, M04-09): list, record against open invoices, and view. */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, Plus, Send, Trash2, Undo2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Decimal, amount, dec, parseAmount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { openFile, uploadFile } from '../../lib/files';
import { manilaToday, useAccounts, useAtcs, useBranches, useContacts } from '../../lib/queries';
import { ReasonDialog } from '../../components/ReasonDialog';
import { Alert, Button, Card, EmptyState, Field, FilterBar, Modal, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';
import type { SalesSummary } from './types';

export interface ReceiptView {
  id: string;
  number: string | null;
  serial: string | null;
  status: 'DRAFT' | 'POSTED' | 'VOIDED';
  branchId: string;
  contactId: string;
  customer?: string;
  date: string;
  method: string;
  cashAccountId: string;
  amountReceived: string;
  cwtAmount: string;
  cwtAtc: string | null;
  vatWithheld: string;
  unallocated: string;
  checkNumber: string;
  checkDate: string | null;
  checkBank: string;
  reference: string;
  notes: string;
  form2307Received: boolean;
  form2307FileId: string | null;
  pending2307: boolean;
  journalEntryId: string | null;
  voidReason: string | null;
  rowVersion: number;
  allocations: { invoiceId: string; amount: string; serial: string | null; kind?: string; invoiceTotal: string | null; invoiceBalance: string | null }[];
}

export const METHOD_LABEL: Record<string, string> = {
  CASH: 'Cash',
  CHECK: 'Check',
  BANK_TRANSFER: 'Bank transfer',
  GCASH: 'GCash',
  MAYA: 'Maya',
  CARD: 'Card',
  OTHER: 'Other',
};

const TONE = { DRAFT: 'neutral', POSTED: 'ok', VOIDED: 'critical' } as const;

export function ReceiptsPage(): JSX.Element {
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [pending, setPending] = useState(false);
  const [page, setPage] = useState(1);
  const params = new URLSearchParams({ page: String(page), pageSize: '50' });
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  if (pending) params.set('pending2307', 'true');
  const list = useQuery({
    queryKey: ['receipts', params.toString()],
    queryFn: () => api<{ data: ReceiptView[]; total: number; pageSize: number }>(`/receipts?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.pageSize)) : 1;
  return (
    <>
      <PageHeader
        title="Receipts"
        description="Payments from customers, including the tax they withheld. Receipts with CWT stay flagged until their 2307 arrives."
        actions={can('receipts.create', 'receipts.manage') && <Link to="/receipts/new"><Button><Plus aria-hidden="true" className="h-4 w-4" /> Record receipt</Button></Link>}
      />
      <FilterBar>
        <input className={`${filterClass} w-56`} placeholder="Number, check no. or customer" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search" />
        <select className={filterClass} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="Status">
          <option value="">All statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="POSTED">Posted</option>
          <option value="VOIDED">Voided</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input type="checkbox" className="h-4 w-4" checked={pending} onChange={(e) => { setPending(e.target.checked); setPage(1); }} /> 2307 pending
        </label>
      </FilterBar>
      <Card>
        {list.isLoading && <Skeleton className="h-48 w-full" />}
        {list.data?.data.length === 0 && <EmptyState title="No receipts" />}
        {list.data && list.data.data.length > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <Th>Date</Th>
                  <Th>Number</Th>
                  <Th>Customer</Th>
                  <Th>Method</Th>
                  <Th align="right">Cash</Th>
                  <Th align="right">CWT</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {list.data.data.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <Td className="whitespace-nowrap">{shortDate(r.date)}</Td>
                    <Td className="whitespace-nowrap font-medium"><Link to={`/receipts/${r.id}`} className="text-brand-700 hover:underline dark:text-brand-100">{r.number ?? 'Draft'}</Link></Td>
                    <Td className="max-w-xs truncate">{r.customer}</Td>
                    <Td>{METHOD_LABEL[r.method]}{r.checkNumber ? ` #${r.checkNumber}` : ''}</Td>
                    <Td align="right"><Money>{amount(r.amountReceived)}</Money></Td>
                    <Td align="right"><Money>{amount(r.cwtAmount, true)}</Money>{r.pending2307 && <span className="ml-2"><StatusChip tone="warning">2307 pending</StatusChip></span>}</Td>
                    <Td><StatusChip tone={TONE[r.status]}>{r.status === 'DRAFT' ? 'Draft' : r.status === 'POSTED' ? 'Posted' : 'Voided'}</StatusChip></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <div className="mt-4 flex items-center justify-end gap-2 text-sm text-slate-500">
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span>Page {page} of {pages}</span>
              <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}

export function ReceiptEditorPage(): JSX.Element {
  const { id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const customers = useContacts('customer');
  const branches = useBranches();
  const accounts = useAccounts();
  const atcs = useAtcs();
  const existing = useQuery({ queryKey: ['receipt', id], queryFn: () => api<ReceiptView>(`/receipts/${id}`), enabled: Boolean(id) });

  const [contactId, setContactId] = useState(search.get('contact') ?? '');
  const [branchId, setBranchId] = useState('');
  const [date, setDate] = useState(manilaToday());
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [cashAccountId, setCashAccountId] = useState('');
  const [amountReceived, setAmountReceived] = useState('');
  const [cwtAmount, setCwtAmount] = useState('');
  const [cwtAtc, setCwtAtc] = useState('');
  const [vatWithheld, setVatWithheld] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [checkBank, setCheckBank] = useState('');
  const [reference, setReference] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [error, setError] = useState<ApiError | null>(null);

  const customer = customers.data?.find((c) => c.id === contactId);
  const open = useQuery({
    queryKey: ['sales', 'open', contactId],
    queryFn: async () => {
      const [inv, dm] = await Promise.all([
        api<{ data: SalesSummary[] }>(`/sales-documents?kind=SALES_INVOICE&status=ISSUED&openOnly=true&contactId=${contactId}&pageSize=200`),
        api<{ data: SalesSummary[] }>(`/sales-documents?kind=DEBIT_MEMO&status=ISSUED&openOnly=true&contactId=${contactId}&pageSize=200`),
      ]);
      return [...inv.data, ...dm.data].sort((a, b) => (a.date < b.date ? -1 : 1));
    },
    enabled: Boolean(contactId),
  });

  useEffect(() => {
    if (!branchId && branches.data?.length) setBranchId(branches.data.find((b) => b.isHeadOffice)?.id ?? branches.data[0].id);
  }, [branches.data, branchId]);
  useEffect(() => {
    const r = existing.data;
    if (!r) return;
    setContactId(r.contactId);
    setBranchId(r.branchId);
    setDate(r.date);
    setMethod(r.method);
    setCashAccountId(r.cashAccountId);
    setAmountReceived(dec(r.amountReceived).toFixed(2));
    setCwtAmount(dec(r.cwtAmount).isZero() ? '' : dec(r.cwtAmount).toFixed(2));
    setCwtAtc(r.cwtAtc ?? '');
    setVatWithheld(dec(r.vatWithheld).isZero() ? '' : dec(r.vatWithheld).toFixed(2));
    setCheckNumber(r.checkNumber);
    setCheckDate(r.checkDate ?? '');
    setCheckBank(r.checkBank);
    setReference(r.reference);
    setAlloc(Object.fromEntries(r.allocations.map((a) => [a.invoiceId, dec(a.amount).toFixed(2)])));
  }, [existing.data]);
  useEffect(() => {
    const invoice = search.get('invoice');
    if (!id && invoice && open.data && !(invoice in alloc)) {
      const inv = open.data.find((x) => x.id === invoice);
      if (inv) setAlloc({ [invoice]: dec(inv.balanceDue).toFixed(2) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open.data]);
  useEffect(() => {
    if (customer?.expectedCwtAtc && !cwtAtc) setCwtAtc(customer.expectedCwtAtc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const allocated = useMemo(() => Object.values(alloc).reduce((a, v) => a.plus(dec(parseAmount(v) ?? 0)), new Decimal(0)), [alloc]);
  const settled = dec(parseAmount(amountReceived) ?? 0).plus(dec(parseAmount(cwtAmount) ?? 0)).plus(dec(parseAmount(vatWithheld) ?? 0));
  const leftover = settled.minus(allocated);

  /** CWT on the allocated invoices: ATC rate × their amount net of VAT. */
  const suggestCwt = (): void => {
    const atc = atcs.data?.find((a) => a.code === cwtAtc);
    if (!atc || !open.data) return;
    let base = new Decimal(0);
    for (const inv of open.data) {
      const share = dec(parseAmount(alloc[inv.id] ?? '') ?? 0);
      if (share.isZero()) continue;
      const net = dec(inv.totalAmount).minus(dec(inv.vatAmount));
      base = base.plus(share.times(net).div(dec(inv.totalAmount)));
    }
    const cwt = base.times(dec(atc.rate)).toDecimalPlaces(2);
    setCwtAmount(cwt.toFixed(2));
    setAmountReceived(allocated.minus(cwt).minus(dec(parseAmount(vatWithheld) ?? 0)).toFixed(2));
  };

  const save = useMutation({
    mutationFn: async (then: 'draft' | 'post') => {
      const body = {
        branchId,
        contactId,
        date,
        method,
        cashAccountId,
        amountReceived: parseAmount(amountReceived) ?? '0',
        cwtAmount: parseAmount(cwtAmount) ?? undefined,
        cwtAtc: cwtAmount && dec(parseAmount(cwtAmount) ?? 0).gt(0) ? cwtAtc || null : null,
        vatWithheld: parseAmount(vatWithheld) ?? undefined,
        checkNumber,
        checkDate: checkDate || null,
        checkBank,
        reference,
        rowVersion: existing.data?.rowVersion,
        allocations: Object.entries(alloc).flatMap(([invoiceId, v]) => (parseAmount(v) && dec(parseAmount(v)!).gt(0) ? [{ invoiceId, amount: parseAmount(v)! }] : [])),
      };
      let r = id ? await api<ReceiptView>(`/receipts/${id}`, { method: 'PATCH', body }) : await api<ReceiptView>('/receipts', { method: 'POST', body });
      if (then === 'post') r = await api<ReceiptView>(`/receipts/${r.id}/post`, { method: 'POST', body: {} });
      return r;
    },
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['receipts'] });
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      navigate(`/receipts/${r.id}`, { replace: true });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'Could not save.')),
  });

  if (id && existing.isLoading) return <Skeleton className="h-96 w-full" />;
  const cashAccounts = (accounts.data ?? []).filter((a) => a.isCash && a.isPosting && a.active);

  return (
    <>
      <PageHeader title={id ? 'Edit receipt' : 'Record a receipt'} description="Allocate what came in to the customer's open invoices. The tax they withheld counts toward settling the invoice." />
      {error && <div className="mb-4"><Alert tone="critical">{error.message}{error.fieldMessages.length > 1 && <ul className="mt-1 list-disc pl-5">{error.fieldMessages.slice(1, 5).map((m) => <li key={m}>{m}</li>)}</ul>}</Alert></div>}
      <Card className="mb-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="md:col-span-2">
            <Field label="Customer" required>
              <select className={inputClass} value={contactId} onChange={(e) => { setContactId(e.target.value); setAlloc({}); setCwtAtc(''); }}>
                <option value="">Choose a customer</option>
                {(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.registeredName}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Date received" required><input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Branch">
            <select className={inputClass} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {(branches.data ?? []).filter((b) => b.active).map((b) => <option key={b.id} value={b.id}>{b.code} {b.name}</option>)}
            </select>
          </Field>
          <Field label="Method" required>
            <select className={inputClass} value={method} onChange={(e) => setMethod(e.target.value)}>
              {Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Deposited to" required error={error?.fields.cashAccountId}>
            <select className={inputClass} value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)}>
              <option value="">Choose an account</option>
              {cashAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </Field>
          {method === 'CHECK' && (
            <>
              <Field label="Check no." required error={error?.fields.checkNumber}><input className={inputClass} value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} /></Field>
              <Field label="Check date"><input type="date" className={inputClass} value={checkDate} onChange={(e) => setCheckDate(e.target.value)} /></Field>
              <Field label="Bank"><input className={inputClass} value={checkBank} onChange={(e) => setCheckBank(e.target.value)} /></Field>
            </>
          )}
          <Field label="Reference"><input className={inputClass} value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
        </div>
      </Card>

      <Card title="Open invoices" className="mb-4">
        {!contactId && <EmptyState title="Choose the customer first" />}
        {contactId && open.isLoading && <Skeleton className="h-24 w-full" />}
        {contactId && open.data?.length === 0 && <EmptyState title="Nothing open" description="Anything received will be held as a customer advance." />}
        {open.data && open.data.length > 0 && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Invoice</Th>
                <Th>Date</Th>
                <Th>Due</Th>
                <Th align="right">Total</Th>
                <Th align="right">Balance</Th>
                <Th align="right">Apply</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {open.data.map((inv) => (
                <tr key={inv.id}>
                  <Td className="whitespace-nowrap font-medium">{inv.serial}</Td>
                  <Td className="whitespace-nowrap">{shortDate(inv.date)}</Td>
                  <Td className="whitespace-nowrap">{shortDate(inv.dueDate)}</Td>
                  <Td align="right"><Money>{amount(inv.totalAmount)}</Money></Td>
                  <Td align="right"><Money>{amount(inv.balanceDue)}</Money></Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" className="text-xs text-brand-700 hover:underline dark:text-brand-100" onClick={() => setAlloc((a) => ({ ...a, [inv.id]: dec(inv.balanceDue).toFixed(2) }))}>Full</button>
                      <input className={`${filterClass} w-32 text-right tabular-nums`} inputMode="decimal" value={alloc[inv.id] ?? ''} onChange={(e) => setAlloc((a) => ({ ...a, [inv.id]: e.target.value }))} aria-label={`Apply to ${inv.serial}`} />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <Card title="What was received">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Field label="Cash received" required error={error?.fields.amountReceived}>
            <input className={`${inputClass} text-right tabular-nums`} inputMode="decimal" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} />
          </Field>
          <Field label="Tax withheld (CWT)" hint="Deducted by the customer; you'll get a 2307.">
            <input className={`${inputClass} text-right tabular-nums`} inputMode="decimal" value={cwtAmount} onChange={(e) => setCwtAmount(e.target.value)} />
          </Field>
          <Field label="ATC" error={error?.fields.cwtAtc}>
            <div className="flex gap-2">
              <select className={inputClass} value={cwtAtc} onChange={(e) => setCwtAtc(e.target.value)}>
                <option value="">—</option>
                {(atcs.data ?? []).filter((a) => a.kind === 'EWT').map((a) => <option key={a.code} value={a.code}>{a.code} {(Number(a.rate) * 100).toFixed(0)}%</option>)}
              </select>
              <Button type="button" variant="secondary" size="sm" disabled={!cwtAtc || allocated.isZero()} onClick={suggestCwt} title="Compute CWT on the invoices applied">Compute</Button>
            </div>
          </Field>
          {customer?.isGovernment && (
            <Field label="Final VAT withheld (5%)" hint="Government agencies withhold this.">
              <input className={`${inputClass} text-right tabular-nums`} inputMode="decimal" value={vatWithheld} onChange={(e) => setVatWithheld(e.target.value)} />
            </Field>
          )}
        </div>
        <dl className="ml-auto mt-4 max-w-xs space-y-1 text-sm">
          <div className="flex justify-between"><dt className="text-slate-500">Settles</dt><dd><Money>{amount(settled.toString())}</Money></dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Applied to invoices</dt><dd><Money>{amount(allocated.toString())}</Money></dd></div>
          <div className={`flex justify-between font-semibold ${leftover.isNegative() ? 'text-red-600' : ''}`}>
            <dt>{leftover.isNegative() ? 'Over-applied by' : 'Customer advance'}</dt>
            <dd><Money>{amount(leftover.abs().toString())}</Money></dd>
          </div>
        </dl>
      </Card>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="secondary" busy={save.isPending && save.variables === 'draft'} disabled={!contactId || !cashAccountId || leftover.isNegative()} onClick={() => { setError(null); save.mutate('draft'); }}>Save draft</Button>
        <Button busy={save.isPending && save.variables === 'post'} disabled={!contactId || !cashAccountId || leftover.isNegative()} onClick={() => { setError(null); save.mutate('post'); }}>Save and post</Button>
      </div>
    </>
  );
}

export function ReceiptDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'void' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const r = useQuery({ queryKey: ['receipt', id], queryFn: () => api<ReceiptView>(`/receipts/${id}`) });
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['receipt', id] });
    void queryClient.invalidateQueries({ queryKey: ['receipts'] });
    void queryClient.invalidateQueries({ queryKey: ['sales'] });
  };
  const fail = (caught: unknown): void => setError(caught instanceof ApiError ? caught.message : caught instanceof Error ? caught.message : 'That did not work.');
  const post = useMutation({ mutationFn: () => api(`/receipts/${id}/post`, { method: 'POST', body: {} }), onSuccess: refresh, onError: fail });
  const voidIt = useMutation({ mutationFn: ({ reason, date }: { reason: string; date?: string }) => api(`/receipts/${id}/void`, { method: 'POST', body: { reason, date } }), onSuccess: () => { setDialog(null); refresh(); }, onError: fail });
  const discard = useMutation({ mutationFn: () => api(`/receipts/${id}`, { method: 'DELETE' }), onSuccess: () => navigate('/receipts', { replace: true }), onError: fail });
  const attach = useMutation({
    mutationFn: async (file: File) => {
      const uploaded = await uploadFile(file, 'form_2307', id);
      return api(`/receipts/${id}/attach-2307`, { method: 'POST', body: { fileId: uploaded.id } });
    },
    onSuccess: refresh,
    onError: fail,
  });

  if (r.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!r.data) return <Alert tone="critical">Could not load this receipt.</Alert>;
  const x = r.data;
  return (
    <>
      <PageHeader
        title={`Receipt ${x.number ?? '(draft)'}`}
        description={`${x.customer} · ${shortDate(x.date)} · ${METHOD_LABEL[x.method]}${x.checkNumber ? ` #${x.checkNumber}` : ''}`}
        actions={
          <>
            <StatusChip tone={TONE[x.status]}>{x.status === 'DRAFT' ? 'Draft' : x.status === 'POSTED' ? 'Posted' : 'Voided'}</StatusChip>
            {x.status === 'DRAFT' && can('receipts.create', 'receipts.manage') && (
              <>
                <Link to={`/receipts/${x.id}/edit`}><Button variant="secondary">Edit</Button></Link>
                <Button variant="secondary" onClick={() => setDialog('discard')}><Trash2 aria-hidden="true" className="h-4 w-4" /> Discard</Button>
                <Button busy={post.isPending} onClick={() => post.mutate()}><Send aria-hidden="true" className="h-4 w-4" /> Post</Button>
              </>
            )}
            {x.status === 'POSTED' && x.pending2307 && can('receipts.manage') && (
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-700">
                <FileUp aria-hidden="true" className="h-4 w-4" /> {attach.isPending ? 'Uploading…' : 'Attach 2307'}
                <input type="file" accept="application/pdf,image/*" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) attach.mutate(f); }} />
              </label>
            )}
            {x.status === 'POSTED' && can('void.approve') && <Button variant="danger" onClick={() => setDialog('void')}><Undo2 aria-hidden="true" className="h-4 w-4" /> Void</Button>}
          </>
        }
      />
      {error && !dialog && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      {x.pending2307 && <div className="mb-4"><Alert tone="warning">The customer withheld ₱{amount(x.cwtAmount)} under {x.cwtAtc}. Attach their BIR Form 2307 when it arrives — you need it to claim the credit.</Alert></div>}
      {x.status === 'VOIDED' && <div className="mb-4"><Alert tone="critical">Voided: {x.voidReason}. The invoices it paid are open again.</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Card title="Applied to">
          {x.allocations.length === 0 ? <EmptyState title="Not applied to any invoice" description="The whole amount is a customer advance." /> : (
            <TableWrap>
              <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Invoice</Th><Th align="right">Invoice total</Th><Th align="right">Applied</Th><Th align="right">Still owed</Th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {x.allocations.map((a) => (
                  <tr key={a.invoiceId}>
                    <Td><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/sales/${a.invoiceId}`}>{a.serial}</Link></Td>
                    <Td align="right"><Money>{amount(a.invoiceTotal)}</Money></Td>
                    <Td align="right"><Money>{amount(a.amount)}</Money></Td>
                    <Td align="right"><Money>{amount(a.invoiceBalance)}</Money></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
        <Card title="Amounts">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Cash received</dt><dd><Money>{amount(x.amountReceived)}</Money></dd></div>
            {!dec(x.cwtAmount).isZero() && <div className="flex justify-between"><dt className="text-slate-500">CWT ({x.cwtAtc})</dt><dd><Money>{amount(x.cwtAmount)}</Money></dd></div>}
            {!dec(x.vatWithheld).isZero() && <div className="flex justify-between"><dt className="text-slate-500">Final VAT withheld</dt><dd><Money>{amount(x.vatWithheld)}</Money></dd></div>}
            {!dec(x.unallocated).isZero() && <div className="flex justify-between"><dt className="text-slate-500">Customer advance</dt><dd><Money>{amount(x.unallocated)}</Money></dd></div>}
            {x.form2307FileId && <div><dt className="text-slate-500">2307</dt><dd><button className="text-brand-700 hover:underline dark:text-brand-100" onClick={() => void openFile(`/files/${x.form2307FileId}`).catch(fail)}>Open the certificate</button></dd></div>}
            {x.journalEntryId && <div><dt className="text-slate-500">Ledger</dt><dd><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/journals/${x.journalEntryId}`}>View journal entry</Link></dd></div>}
          </dl>
        </Card>
      </div>
      {dialog === 'void' && (
        <ReasonDialog title={`Void receipt ${x.number}?`} description="Use this for a bounced check too. The journal is reversed and the invoices reopen." confirmLabel="Void receipt" withDate error={error} busy={voidIt.isPending} onClose={() => { setDialog(null); setError(null); }} onConfirm={(reason, date) => voidIt.mutate({ reason, date })} />
      )}
      {dialog === 'discard' && (
        <Modal title="Discard this draft?" onClose={() => setDialog(null)}>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDialog(null)}>Keep it</Button>
            <Button variant="danger" busy={discard.isPending} onClick={() => discard.mutate()}>Discard</Button>
          </div>
        </Modal>
      )}
    </>
  );
}

